import { StandardKeyboardEvent } from "../../../base/browser/keyboardEvent.js";
import { Emitter } from "../../../base/common/events.js";
import { Disposable, DisposableStore, IDisposable, toDisposable } from "../../../base/common/lifecycle.js";
import { ILogService } from "../../log/common/log.js";
import { IWindowsMainService } from "../../windows/common/windows.js";
import { IKeyboardEventWithSource, IKeyboardService, isModifierKey, KeyboardListener, KeyboardListenerOptions, KeyCode, KeyPressedOptions, ModifierKey } from "../common/keyboardService.js";

const routingKey = Symbol.for("needle.editor.keyboardRouting");
type KeyboardRouting = { active?: object; editors: Set<object>; services: Set<object> };
type RoutingWindow = Window & { [routingKey]?: KeyboardRouting };

export type BrowserKeyboardServiceOptions = {
    /**
     * Chrome-extension mode: emit key EVENTS only when the event's composed path crosses
     * a root registered via {@link registerScopeRoot} — the editor is injected into a
     * third-party page and typing into the HOST page must not trigger editor commands
     * (nor may our CTRL+S preventDefault the host's save). Until a root is registered
     * (UI not mounted yet) nothing is emitted. `isKeyPressed` state is never scoped.
     */
    scopeEventsToRegisteredRoots?: boolean;
};

export class BrowserKeyboardService extends Disposable implements IKeyboardService {

    isKeyPressed(key: KeyCode | ModifierKey, options?: KeyPressedOptions): boolean {
        // Pressed-key state is global (we listen in every window); an opt-in focus
        // check lets a single-window consumer ask "held AND meant for me".
        if (options?.window && !options.window.document.hasFocus()) return false;
        if (isModifierKey(key)) {
            switch (key) {
                case "Shift":
                    return this.keysPressed.has("ShiftLeft") || this.keysPressed.has("ShiftRight");
                case "Control":
                    return this.keysPressed.has("ControlLeft") || this.keysPressed.has("ControlRight");
                case "Alt":
                    return this.keysPressed.has("AltLeft") || this.keysPressed.has("AltRight");
                case "Meta":
                    return this.keysPressed.has("MetaLeft") || this.keysPressed.has("MetaRight");
            }
        }
        return this.keysPressed.has(key);
    }

    private keysPressed: Set<string> = new Set();

    // Two emitters per event: the SCOPED one respects `scopeEventsToRegisteredRoots`
    // (keybindings/commands); the UNSCOPED one fires for every key. A subscription
    // picks between them via `options.scoped` (default: scoped). See interface.
    private readonly _onKeyDown = new Emitter<IKeyboardEventWithSource>();
    private readonly _onKeyDownUnscoped = new Emitter<IKeyboardEventWithSource>();
    private readonly _onKeyUp = new Emitter<IKeyboardEventWithSource>();
    private readonly _onKeyUpUnscoped = new Emitter<IKeyboardEventWithSource>();
    private readonly _onKeyPress = new Emitter<IKeyboardEventWithSource>();
    private readonly _onKeyPressUnscoped = new Emitter<IKeyboardEventWithSource>();

    onKeyDown(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable {
        return (options?.scoped === false ? this._onKeyDownUnscoped : this._onKeyDown).event(listener);
    }
    onKeyUp(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable {
        return (options?.scoped === false ? this._onKeyUpUnscoped : this._onKeyUp).event(listener);
    }
    onKeyPress(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable {
        return (options?.scoped === false ? this._onKeyPressUnscoped : this._onKeyPress).event(listener);
    }

    private readonly scopeEvents: boolean;
    private readonly scopeRoots = new Set<HTMLElement>();

    /** listeners per window — ADDITIVE: every open window is listened to at once (the
     * previous last-window-wins behavior left the main window keyboard-dead the moment
     * a popout opened — CTRL+Z with the 3d viewport focused did nothing). */
    private readonly attachedWindows = new Map<Window, DisposableStore>();
    private routing?: KeyboardRouting;
    private readonly routeToken = {};

    constructor(
        options: BrowserKeyboardServiceOptions | undefined,
        @ILogService private readonly logger: ILogService,
        @IWindowsMainService windowsMainService: IWindowsMainService
    ) {
        super();

        this.scopeEvents = options?.scopeEventsToRegisteredRoots === true;

        this._register(windowsMainService.onDidOpenWindow((evt) => {
            logger.trace(`KeyboardService: attaching to opened window`);
            this.attach(evt.getWindow());
        }));
        this._register(windowsMainService.onDidDestroyWindow((evt) => {
            logger.trace(`KeyboardService: detaching from destroyed window`);
            this.detach(evt.getWindow());
        }));

        const home = globalThis.window;
        const ancestors: Window[] = [];
        if (this.scopeEvents) {
            let current: Window = home;
            while (current.parent !== current) {
                try {
                    // Access is required before installing listeners across a frame boundary.
                    void current.parent.document;
                } catch (error) {
                    if ((error as { name?: string }).name !== "SecurityError") throw error;
                    logger.debug("KeyboardService: cross-origin parent requires extension routing");
                    break;
                }
                current = current.parent;
                ancestors.push(current);
            }
            // Shared on the window, so separate editor bundles also agree on one recipient.
            const host = current as RoutingWindow;
            this.routing = host[routingKey] ??= { editors: new Set(), services: new Set() };
            this.routing.services.add(this.routeToken);
            this._register(toDisposable(() => {
                this.routing!.editors.delete(this.routeToken);
                this.routing!.services.delete(this.routeToken);
                if (this.routing!.active === this.routeToken) this.routing!.active = this.routing!.editors.values().next().value;
                if (!this.routing!.services.size) delete host[routingKey];
            }));
        }
        this.attach(home);
        for (const ancestor of ancestors) this.attach(ancestor, true);
    }

    registerScopeRoot(root: HTMLElement): IDisposable {
        this.scopeRoots.add(root);
        if (this.routing) {
            this.routing.editors.add(this.routeToken);
            this.routing.active ??= this.routeToken;
        }
        return toDisposable(() => {
            this.scopeRoots.delete(root);
            if (!this.scopeRoots.size && this.routing) {
                this.routing.editors.delete(this.routeToken);
                if (this.routing.active === this.routeToken) this.routing.active = this.routing.editors.values().next().value;
            }
        });
    }

    isEventInScope(event: KeyboardEvent): boolean {
        return this.shouldEmit(event);
    }

    private attach(window: Window, ancestor = false): void {
        if (this.attachedWindows.has(window)) return;
        const store = new DisposableStore();
        this.attachedWindows.set(window, store);
        this._register(store);

        // Handlers are created PER WINDOW so each event can be tagged with the
        // window it came from (see IKeyboardEventWithSource) — a shared handler
        // would lose that, forcing consumers to re-derive it from the DOM.
        const accepts = () => !this.routing || (this.scopeRoots.size > 0 && this.routing.active === this.routeToken);
        const onKeyDown = (event: KeyboardEvent) => { if (accepts()) this.__onKeyDown(event, window); };
        const onKeyUp = (event: KeyboardEvent) => {
            this.keysPressed.delete(event.code);
            if (accepts()) this.__onKeyUp(event, window);
        };
        const onKeyPress = (event: KeyboardEvent) => { if (accepts()) this.__onKeyPress(event, window); };
        if (!ancestor) {
            const activate = () => { if (this.routing && this.scopeRoots.size) this.routing.active = this.routeToken; };
            for (const type of ["pointerdown", "focusin"]) {
                window.addEventListener(type, activate, { capture: true });
                store.add(toDisposable(() => window.removeEventListener(type, activate, { capture: true })));
            }
        }
        const clearKeys = () => this.keysPressed.clear();
        window.addEventListener("blur", clearKeys);
        store.add(toDisposable(() => window.removeEventListener("blur", clearKeys)));

        window.addEventListener('keydown', onKeyDown, { capture: true });
        store.add(toDisposable(() => {
            window.removeEventListener('keydown', onKeyDown, { capture: true });
        }));

        window.addEventListener('keyup', onKeyUp, { capture: true });
        store.add(toDisposable(() => {
            window.removeEventListener('keyup', onKeyUp, { capture: true });
        }));

        window.addEventListener('keypress', onKeyPress, { capture: true });
        store.add(toDisposable(() => {
            window.removeEventListener('keypress', onKeyPress, { capture: true });
        }));
    }

    private detach(window: Window): void {
        const store = this.attachedWindows.get(window);
        if (!store) return;
        this.attachedWindows.delete(window);
        store.dispose();
    }

    /** Whether this key event may drive keybindings/commands (see {@link registerScopeRoot}). */
    private shouldEmit(event: KeyboardEvent): boolean {
        if (!this.scopeEvents) return true;
        if (this.scopeRoots.size === 0) return false;
        for (const target of event.composedPath()) {
            if (this.scopeRoots.has(target as HTMLElement)) return true;
        }
        return false;
    }

    /** Tag an event with the window it was observed in (see {@link IKeyboardEventWithSource}). */
    private toKeyboardEvent(event: KeyboardEvent, window: Window): IKeyboardEventWithSource {
        return Object.assign(new StandardKeyboardEvent(event), { sourceWindow: window });
    }

    private __onKeyDown = (event: KeyboardEvent, window: Window) => {
        const standard = this.toKeyboardEvent(event, window);
        // Unscoped first, so a swallow-subscriber (scoped:false) can stopPropagation
        // before the scoped (keybinding) stream and the host page ever see the event.
        this._onKeyDownUnscoped.fire(standard);
        if (this.shouldEmit(event)) {
            this._onKeyDown.fire(standard);
        }
        this.keysPressed.add(event.code);
    }
    private __onKeyUp = (event: KeyboardEvent, window: Window) => {
        const standard = this.toKeyboardEvent(event, window);
        this._onKeyUpUnscoped.fire(standard);
        if (this.shouldEmit(event)) {
            this._onKeyUp.fire(standard);
        }
        this.keysPressed.delete(event.code);
    }
    private __onKeyPress = (event: KeyboardEvent, window: Window) => {
        const standard = this.toKeyboardEvent(event, window);
        this._onKeyPressUnscoped.fire(standard);
        if (this.shouldEmit(event)) {
            this._onKeyPress.fire(standard);
        }
    }
}
